import { IMsfFormBuilder } from '@components/form-builder/form-builder';

export interface AssignmentDataRecordFilterRow {
  id: string;
  label: string | null;
  database: string;
  table: string;
  kind: string;
  state: string | null;
  operation: string;
}

export interface AssignmentDataRecordFilterModel {
  search?: string | null;
  database?: string[] | string | null;
  table?: string[] | string | null;
  kind?: string[] | string | null;
  state?: string[] | string | null;
  operation?: string[] | string | null;
}

export type AssignmentDataRecordFilterField =
  | 'database'
  | 'table'
  | 'kind'
  | 'state'
  | 'operation';

export function emptyAssignmentDataRecordFilter(): AssignmentDataRecordFilterModel {
  return {};
}

export function uniqueAssignmentDataRecordValues(
  rows: AssignmentDataRecordFilterRow[],
  field: AssignmentDataRecordFilterField,
): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const value = row[field];
    if (typeof value === 'string' && value.trim()) {
      values.add(value);
    }
  }
  return [...values].sort((left, right) => left.localeCompare(right));
}

export function toNamedSelectOptions(values: string[]): { id: string; name: string }[] {
  return values.map(id => ({ id, name: id }));
}

export function filterAssignmentDataRecords<T extends AssignmentDataRecordFilterRow>(
  rows: T[],
  filter: AssignmentDataRecordFilterModel,
): T[] {
  const search = (filter.search ?? '').trim().toLowerCase();
  return rows.filter(row => {
    if (!includesSelected(filter.database, row.database)) {
      return false;
    }
    if (!includesSelected(filter.table, row.table)) {
      return false;
    }
    if (!includesSelected(filter.kind, row.kind)) {
      return false;
    }
    if (!includesSelected(filter.state, row.state)) {
      return false;
    }
    if (!includesSelected(filter.operation, row.operation)) {
      return false;
    }
    if (!search) {
      return true;
    }
    return assignmentDataRecordSearchText(row).includes(search);
  });
}

export function buildAssignmentDataRecordFilterFields(): IMsfFormBuilder[] {
  return [
    {
      name: 'search',
      type: 'input',
      placeholder: 'Search by record or id',
      options: { col: 'col-12 md:col-6 xl:col-3' },
    },
    selectField('table', 'Table', 'none'),
    selectField('kind', 'Kind', 'locale'),
    selectField('state', 'Status', 'locale'),
    selectField('operation', 'Operation', 'locale'),
    selectField('database', 'Database', 'none'),
  ];
}

export function syncAssignmentDataRecordFilterOptions(
  fields: IMsfFormBuilder[],
  rows: AssignmentDataRecordFilterRow[],
): void {
  for (const field of fields) {
    if (!isFilterField(field.name)) {
      continue;
    }
    const next = toNamedSelectOptions(uniqueAssignmentDataRecordValues(rows, field.name));
    if (!sameNamedOptions(field.selectOptions, next)) {
      field.selectOptions = next;
    }
  }
}

function selectField(
  name: AssignmentDataRecordFilterField,
  placeholder: string,
  translateBy: 'locale' | 'none',
): IMsfFormBuilder {
  const field: IMsfFormBuilder = {
    name,
    type: 'multi-select',
    placeholder,
    selectOptions: [],
    options: {
      clearable: true,
      col: 'col-12 md:col-6 xl:col-3',
      select: {
        optionLabel: 'name',
        optionValue: 'id',
        translateBy,
      },
    },
  };
  field.hide = () => (field.selectOptions?.length ?? 0) < 2;
  return field;
}

function isFilterField(name: string): name is AssignmentDataRecordFilterField {
  return name === 'database'
    || name === 'table'
    || name === 'kind'
    || name === 'state'
    || name === 'operation';
}

function sameNamedOptions(
  left: { id?: string; name?: string }[] | undefined,
  right: { id: string; name: string }[],
): boolean {
  if (!left || left.length !== right.length) {
    return false;
  }
  return left.every((item, index) => item.id === right[index].id && item.name === right[index].name);
}

function assignmentDataRecordSearchText(row: AssignmentDataRecordFilterRow): string {
  return [row.id, row.label, row.table, row.database, row.state, row.operation]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ')
    .toLowerCase();
}

function includesSelected(selected: unknown, value: string | null): boolean {
  const values = selectedValues(selected);
  if (!values) {
    return true;
  }
  return values.includes(value ?? '');
}

function selectedValues(selected: unknown): string[] | null {
  if (selected == null || selected === '') {
    return null;
  }
  if (Array.isArray(selected)) {
    const values = selected.filter((item): item is string => typeof item === 'string' && item.length > 0);
    return values.length ? values : null;
  }
  if (typeof selected === 'string') {
    return [selected];
  }
  return null;
}
